import * as PrismicDOM from 'prismic-dom';
import * as React from 'react';
import {faAngleDown, faAngleUp} from '@fortawesome/pro-solid-svg-icons';
import {FAQPage, WithContext} from 'schema-dts';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {helmetJsonLdProp} from 'react-schemaorg';
import {Helmet} from 'react-helmet-async';
import {PrismicRichText} from '@prismicio/react';
import {RichTextField} from '@prismicio/types';
import {useState} from 'react';

import {Primary, PrismicItem, Slice} from '../cms.types';

export interface ItemsEntity extends PrismicItem {
    answer: RichTextField;
    collapsing: boolean;
    open?: boolean;
    question: string;
    schema_only: boolean;
}

interface FaqSectionSlicePrimary extends Primary {
    hide_all: boolean;
}
export interface FaqSectionSlice extends Slice {
    items: ItemsEntity[];
    primary: FaqSectionSlicePrimary;
    slice_type: `faq_section`;
}

export interface FaqQuestionProps {
    collapsing?: boolean;
    faqItem: ItemsEntity;
    startOpen: boolean;
}

export const FaqQuestion = ({collapsing, faqItem}: FaqQuestionProps) => {
    const [open, setOpen] = useState(false);

    /**
     * Template
     */
    return (
        <div className="tw-border-b tw-px-3 tw-py-2">
            <div
                className="tw-flex tw-justify-between tw-items-center tw-cursor-pointer"
                onClick={() => setOpen(!open)}
            >
                <h5 className="tw-pt-2 tw-font-bold">{faqItem.question}</h5>
                {collapsing && <FontAwesomeIcon icon={open ? faAngleUp : faAngleDown} />}
            </div>
            {open && collapsing && (
                <div className="tw-mt-3 -tw-mb-2">
                    <PrismicRichText field={faqItem.answer} />
                </div>
            )}
            {!collapsing && (
                <div className="tw-mt-3 -tw-mb-2">
                    <PrismicRichText field={faqItem.answer} />
                </div>
            )}
        </div>
    );
};

export interface FaqSectionProps {
    slice: FaqSectionSlice;
}

export const FaqSection = ({slice}: FaqSectionProps) => {
    const mainEntities = [];

    slice.items.forEach((item) => {
        mainEntities.push({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: PrismicDOM.RichText.asText(item.answer),
            },
        });
    });
    const faqSchema: WithContext<FAQPage> = {
        '@context': `https://schema.org`,
        '@type': `FAQPage`,
        mainEntity: mainEntities,
    };

    if (slice.primary.hide_all) {
        return <Helmet script={[helmetJsonLdProp<FAQPage>(faqSchema)]} />;
    }

    /**
     * Template
     */
    return (
        <div className="tw-mb-6 tw-border-t">
            {slice.items
                .filter((faqItem) => !faqItem.schema_only)
                .map((faqItem, index) => (
                    <FaqQuestion
                        collapsing={faqItem.collapsing}
                        faqItem={faqItem}
                        key={index}
                        startOpen={!slice.primary.hide_all}
                    />
                ))}
            <Helmet script={[helmetJsonLdProp<FAQPage>(faqSchema)]} />
        </div>
    );
};
